import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// --- Data Logic ---
import { getServiceBySlug } from '../../Public/services-data.js';

// --- UI Components ---
import Layout from '../../Utils/Navbar/navbar-layout-component.js';
import Footer from '../../Utils/Footer/footer-component.js';
import CommingSoon from '../Error-Screen/ComingSoon-screen.js';

// --- Reusable Sections ---
import MetricsSection from '../../Utils/Sections/section-metrics-component.js';
import HeroSection from '../../Utils/Sections/section-hero-component.js';
import FaqSection from '../../Utils/Sections/section-faq-component.js';
import ServicesSection from './ServicesSection.js';
import ServiceMaintenanceSection from './ServicesMaintananceSection.js';
import ServicesMiddleSection from './ServicesMiddleSection.js';
import InfoCardsSection from '../../Utils/Sections/section-info-cards-component.js';
import LazyOnView from '../../Utils/LazyLoading/LazyOnView.js';
import TypesSection from '../../Utils/Sections/section-types-component.js';
import QuickCallSection from '../../Utils/Sections/section-quick-call-component.js';

// --- Skeleton Loader ---
const ServiceSkeleton = () => (
    <div className="w-full min-h-screen bg-white dark:bg-black flex flex-col animate-pulse">
        <div className="w-full h-[80vh] bg-gray-100 dark:bg-neutral-900 relative flex flex-col items-center justify-center px-6">
            <div className="h-12 md:h-20 w-3/4 max-w-4xl bg-gray-200 dark:bg-neutral-800 rounded-2xl mb-6"></div>
            <div className="h-6 md:h-10 w-1/2 max-w-2xl bg-gray-200 dark:bg-neutral-800 rounded-xl opacity-70"></div>
        </div>
        <div className="w-full h-96 bg-white dark:bg-black mt-10 px-6 max-w-7xl mx-auto">
            <div className="h-full w-full bg-gray-50 dark:bg-neutral-900 rounded-[40px]"></div>
        </div>
    </div>
);

const Service = () => {
    const { service: serviceSlug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [matchedService, setMatchedService] = useState(null);

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            try {
                // 1. Fetch Data
                const service = await getServiceBySlug(serviceSlug);
                setMatchedService(service);

            } catch (err) {
                console.error("Error loading service page:", err);
                setMatchedService(null);
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [serviceSlug]);

    if (loading) return <Layout><ServiceSkeleton /><Footer /></Layout>;
    if (!matchedService) return <Layout><CommingSoon /><Footer /></Layout>;

    // --- Data Mapping ---
    const heroContent = {
        heroTitle: matchedService.hero?.title,
        heroTagline: matchedService.name,
        heroDescription: matchedService.hero?.description,
        heroMedia: matchedService.hero?.imageDesktop,
        heroSubDescription: "2026/2027 SEER2 & Energy Star Compliant",
    };

    return (
        <div className="flex w-full flex-col bg-white min-h-screen font-assistant">
            <Layout seo={matchedService.seo}>
                <Helmet>
                    {matchedService.schema && (
                        <script type="application/ld+json">
                            {JSON.stringify(matchedService.schema)}
                        </script>
                    )}
                </Helmet>

                {/* --- 1. HERO SECTION (Full Width) --- */}
                <div className="w-full">
                    <HeroSection heroContent={heroContent} />
                </div>


                {/* --- 2. METRICS (Full Width Dark Strip) --- */}
                <div className="w-full bg-primary-700 text-white border-y border-neutral-900">
                    <MetricsSection
                        metrics={matchedService.stats}
                    />
                </div>

                {/* --- 3. PROCESS & STORIES --- */}
                {/* Kept relatively open but constrained by the component itself */}
                <div id="process" className="w-full">
                    <ServicesMiddleSection
                        processData={matchedService.process}
                        storiesData={matchedService.stories}
                    />
                </div>

                {/* --- 4. TIPS SLIDER (Boxed Layout) --- */}
                {matchedService.systemTypes && matchedService.systemTypes.types.length > 0 && (
                    <div className="">
                        <div
                            id="tips"
                            className="mx-auto bg-gray-50 dark:bg-[#111] overflow-hidden shadow-sm"
                        >
                            <TypesSection data={matchedService.systemTypes} />
                        </div>
                    </div>
                )}

                <QuickCallSection/>

                {/* --- 5. MAINTENANCE (Boxed Layout) --- */}
                {matchedService.maintenanceTips && (
                    <div className="px-4 md:px-6">
                        <div
                            id="maintenance"
                            className="mx-auto max-w-7xl rounded-[40px] dark:bg-[#111] overflow-hidden "
                        >
                            <ServiceMaintenanceSection data={matchedService.maintenanceTips} />
                        </div>
                    </div>
                )}

                {/* --- 6. EXPLORE OTHER SERVICES (Boxed Layout) --- */}
                <div className="md:px-10 ">
                    <div
                        id="explore"
                        className="mx-auto rounded-[40px] border border-gray-100 dark:border-neutral-900 bg-white dark:bg-black overflow-hidden "
                    >
                        <ServicesSection
                            title="Explore Other Services"
                            isHeadline={false}
                            limit={4}
                            showMore={true}
                            showMoreText="View All Services"
                            onShowMore={() => navigate('/services')}
                            loading={false}
                        />
                    </div>
                </div>

                <LazyOnView rootMargin="-400px 0px -50px 0px" threshold={0.1} delay={0}>
                    <InfoCardsSection />
                </LazyOnView>
                {/* <InfiniteMacbookShowcase /> */}

                {/* --- 7. FAQ SECTION (Constrained Width) --- */}
                {matchedService.faqList && matchedService.faqList.length > 0 && (
                    <div className="bg-slate-50 w-full">
                        <div className="max-w-7xl mx-auto">
                            <FaqSection
                                faqCards={matchedService.faqList.map((faq, i) => ({
                                    id: i,
                                    question: faq.question,
                                    answer: faq.answer
                                }))}
                            />
                        </div>
                    </div>
                )}

            </Layout>
            <Footer />
        </div>
    );
};

export default Service;